/*
 * newsbot - Expanded row content for stories showing extraction data
 */

import { useState } from "react";
import { ChevronRightIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { classNames } from "@utils";
import type { Story, StoryExtraction } from "@/types";

interface StoryExpandedRowProps {
  story: Story;
}

interface MergedExtractions {
  summaries: string[];
  suggested_titles: string[];
  key_facts: string[];
  quotes: Array<{ text: string; source?: string }>;
  entities: string[];
}

function mergeExtractions(extractions: StoryExtraction[] | undefined): MergedExtractions | null {
  if (!extractions?.length) return null;

  const merged: MergedExtractions = {
    summaries: [],
    suggested_titles: [],
    key_facts: [],
    quotes: [],
    entities: [],
  };

  for (const ext of extractions) {
    if (ext.summary) merged.summaries.push(ext.summary);
    if (ext.suggested_titles) merged.suggested_titles.push(...ext.suggested_titles);
    if (ext.key_facts) merged.key_facts.push(...ext.key_facts);
    if (ext.quotes) merged.quotes.push(...ext.quotes);
    if (ext.entities) {
      if (Array.isArray(ext.entities)) {
        merged.entities.push(...ext.entities);
      } else {
        // Handle dict format: { people: [], organizations: [], ... }
        Object.values(ext.entities).forEach((arr) => {
          if (Array.isArray(arr)) merged.entities.push(...arr);
        });
      }
    }
  }

  // Deduplicate
  merged.suggested_titles = [...new Set(merged.suggested_titles)];
  merged.key_facts = [...new Set(merged.key_facts)];
  merged.entities = [...new Set(merged.entities)];

  return merged;
}

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: number;
}

function CollapsibleSection({ title, defaultOpen = false, children, badge }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2 text-left"
      >
        <div className="flex items-center gap-2">
          <ChevronRightIcon
            className={classNames(
              "w-4 h-4 text-gray-400 transition-transform",
              isOpen && "rotate-90"
            )}
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</span>
          {badge !== undefined && badge > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
              {badge}
            </span>
          )}
        </div>
      </button>
      {isOpen && <div className="pb-3 pl-6">{children}</div>}
    </div>
  );
}

export function StoryExpandedRow({ story }: StoryExpandedRowProps) {
  const merged = mergeExtractions(story.extractions);

  if (!merged || (
    merged.summaries.length === 0 &&
    merged.suggested_titles.length === 0 &&
    merged.key_facts.length === 0 &&
    merged.quotes.length === 0 &&
    merged.entities.length === 0
  )) {
    return (
      <tr className="bg-gray-50 dark:bg-gray-850">
        <td colSpan={6} className="pl-12 pr-5 py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No extraction data available for this story.
          </p>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-gray-50 dark:bg-gray-850">
      <td colSpan={6} className="pl-8 pr-5 py-3">
        <div className="max-w-3xl">
          {/* Summary - always shown first if available */}
          {merged.summaries.length > 0 && (
            <CollapsibleSection title="Summary" defaultOpen={true}>
              <div className="space-y-2">
                {merged.summaries.map((summary, idx) => (
                  <p key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                    {summary}
                  </p>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Suggested Titles */}
          {merged.suggested_titles.length > 0 && (
            <CollapsibleSection
              title="Suggested Titles"
              defaultOpen={true}
              badge={merged.suggested_titles.length}
            >
              <ul className="space-y-1">
                {merged.suggested_titles.map((title, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <SparklesIcon className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{title}</span>
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {/* Key Facts */}
          {merged.key_facts.length > 0 && (
            <CollapsibleSection title="Key Facts" badge={merged.key_facts.length}>
              <ul className="space-y-1 list-disc list-inside">
                {merged.key_facts.map((fact, idx) => (
                  <li key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                    {fact}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {/* Quotes */}
          {merged.quotes.length > 0 && (
            <CollapsibleSection title="Quotes" badge={merged.quotes.length}>
              <div className="space-y-2">
                {merged.quotes.map((quote, idx) => (
                  <blockquote
                    key={idx}
                    className="border-l-2 border-gray-300 dark:border-gray-600 pl-3"
                  >
                    <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                      "{quote.text}"
                    </p>
                    {quote.source && (
                      <cite className="text-xs text-gray-500 dark:text-gray-500 not-italic">
                        — {quote.source}
                      </cite>
                    )}
                  </blockquote>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Entities */}
          {merged.entities.length > 0 && (
            <CollapsibleSection title="Entities" badge={merged.entities.length}>
              <div className="flex flex-wrap gap-1">
                {merged.entities.map((entity, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
                  >
                    {entity}
                  </span>
                ))}
              </div>
            </CollapsibleSection>
          )}
        </div>
      </td>
    </tr>
  );
}
